import React, { useMemo, Suspense, useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../Context/AuthContext';
import { useChartTheme } from '../../../Context/ThemeContext';
import { getAllOrgPatientAnalytics } from '../../../Services/AnalyticsService';
import { MdPeople, MdAttachMoney, MdTrendingUp, MdShowChart } from 'react-icons/md';
import AnalyticsChatWidget from '../../../Components/Analytics/AnalyticsChatWidget';
import {
  transformVisitFrequency,
  transformNoShowRisk,
  transformLTVCount,
  transformAcquisitionTrend,
  transformRetentionGauge,
  transformEngagementRadar,
  transformDailyActiveCalendar,
  transformFrequencyRecencySankey,
  transformLTVRevenue,
  transformRecencyFunnel,
  transformCPTPatientDistribution,
} from '../../../Utils/AnalyticsTransformers';
import {
  visitFrequencyBarOption,
  noShowRiskDonutOption,
  recencyHorizontalBarOption,
  ltvCountBarOption,
  acquisitionTrendLineOption,
  retentionGaugeOption,
  engagementRadarOption,
  dailyActiveCalendarOption,
  frequencyRecencySankeyOption,
  ltvRevenueTreemapOption,
  cptPolarEndAngleOption,
} from '../../../Utils/AnalyticsOptions';
import CustomDatePicker from '../../../Components/Common/CustomDatePicker';
import BackButton from '../../../Components/Common/BackButton';

const BaseChart = React.lazy(() => import('../../../Components/Analytics/BaseChart'));

function isEmptyData(d) {
  if (d == null) return true;
  if (typeof d === 'number') return Number.isNaN(d);
  if (Array.isArray(d)) return d.length === 0;
  if (typeof d === 'object') return Object.keys(d).length === 0;
  if (typeof d === 'string') return d.trim().length === 0;
  return false;
}



const formatDateDisplay = (d) => {
  if (!d) return '';
  if (d instanceof Date) {
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
  }

  if (typeof d === 'string') {
    if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(d)) return d;
    if (/^\d{4}-\d{2}-\d{2}$/.test(d)) {
      const dt = new Date(d);
      if (!Number.isNaN(dt.getTime())) return formatDateDisplay(dt);
    }
    const parsed = new Date(d);
    if (!Number.isNaN(parsed.getTime())) return formatDateDisplay(parsed);
    return '';
  }

  return '';
};

const toYMD = (d) => {
  if (!d) return '';
  const dt = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(dt.getTime())) return '';
  const yyyy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const dd = String(dt.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

function SkeletonCard({ height = '340px' }) {
  return (
    <div className="hc-surface rounded-4 p-4 shadow-sm" style={{ minHeight: height }}>
      <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 18, width: '45%', marginBottom: 12 }} />
      <div className="skeleton position-relative overflow-hidden rounded-2 w-100" style={{ height: `calc(${height} - 56px)` }} />
    </div>
  );
}

export default function AnalyticsPatientView({ patientAnalyticsData, loading = false, selectedDateRange = null }) {
  const chartTheme = useChartTheme();
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { token } = useAuth();

  const [localData, setLocalData] = useState(null);
  const [localLoading, setLocalLoading] = useState(false);
  const [localError, setLocalError] = useState(null);

  // Preference: prop > location.state > fetch by params
  // default date window
  const defaultEndDate = new Date();
  const defaultStartDate = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);

  const parseDisplayDate = (v) => {
    if (!v) return null;
    if (v instanceof Date) return v;
    if (typeof v === 'string') {
      // MM/DD/YYYY
      if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(v)) {
        const [m, d, y] = v.split('/').map((s) => Number(s));
        return new Date(y, m - 1, d);
      }
      // YYYY-MM-DD
      if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return new Date(v);
      const parsed = new Date(v);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }
    return null;
  };

  const routeDateRange = location?.state?.date_range || null;
  const initialStartDate = parseDisplayDate(selectedDateRange?.start_date || routeDateRange?.start_date) || defaultStartDate;
  const initialEndDate = parseDisplayDate(selectedDateRange?.end_date || routeDateRange?.end_date) || defaultEndDate;

  const [startDate, setStartDate] = useState(initialStartDate);
  const [endDate, setEndDate] = useState(initialEndDate);

  useEffect(() => {
    if (selectedDateRange) {
      const nextStart = parseDisplayDate(selectedDateRange.start_date);
      const nextEnd = parseDisplayDate(selectedDateRange.end_date);
      if (nextStart) setStartDate(nextStart);
      if (nextEnd) setEndDate(nextEnd);
    }
  }, [selectedDateRange]);

  useEffect(() => {
    if (patientAnalyticsData) return undefined;

    const stateRow = location?.state?.patientAnalyticsRow || null;
    const orgId = params?.organization_id || (stateRow && (stateRow.organization_id || stateRow.OrganizationId || stateRow.organization?.id || stateRow.Organization?.Id));
    const facilityName = params?.facility_name || (stateRow && (stateRow.facility_name || stateRow.FacilityName || stateRow.facilityName));

    if (!orgId) {
      // nothing to fetch
      return undefined;
    }

    let cancelled = false;
    (async () => {
      try {
        setLocalLoading(true);
        setLocalError(null);
        const start = formatDateDisplay(startDate);
        const end = formatDateDisplay(endDate);
        const facilityDecoded = facilityName ? decodeURIComponent(facilityName) : '';
        const res = await getAllOrgPatientAnalytics(token, orgId, start, end, facilityDecoded);
        if (cancelled) return;
        const payload = res?.data ?? null;
        if (payload && facilityName && payload.facility_name && decodeURIComponent(facilityName) !== payload.facility_name) {
  
        }

        setLocalData(payload);
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to load org patient analytics', err);
        setLocalError(err);
        setLocalData(null);
      } finally {
        if (!cancelled) setLocalLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [patientAnalyticsData, location, params, token, selectedDateRange, startDate, endDate]);

  const data = useMemo(() => patientAnalyticsData || localData || null, [patientAnalyticsData, localData]);
  const effectiveLoading = loading || localLoading;

  const routeRow = location?.state?.patientAnalyticsRow || null;
  const routeFacilityName = location?.state?.facility_name || decodeURIComponent(params?.facility_name || '') || routeRow?.facility_name || routeRow?.FacilityName || '';
  const organizationId = params?.organization_id || (routeRow && (routeRow.organization_id || routeRow.OrganizationId || routeRow.organization?.id || routeRow.Organization?.Id)) || null;
  const organizationName = routeRow?.organization_name || routeRow?.OrganizationName || '';

  const dateLabel = useMemo(() => {
    if (!startDate && !endDate) return '';
    return `${formatDateDisplay(startDate)} → ${formatDateDisplay(endDate)}`.trim();
  }, [startDate, endDate]);

  const totalPatients = useMemo(() => data?.total_patients ?? routeRow?.total_patients ?? 0, [data, routeRow]);
  const totalRevenue = useMemo(() => data?.financials?.total_revenue ?? routeRow?.total_revenue ?? 0, [data, routeRow]);
  const retentionRate = useMemo(() => data?.retention_rate ?? routeRow?.retention_rate ?? 0, [data, routeRow]);
  const avgRevenuePerPatient = useMemo(() => data?.averages?.avg_revenue_per_patient ?? routeRow?.avg_revenue_per_patient ?? 0, [data, routeRow]);
  const totalProviders = useMemo(() => {
    const value = data?.provider_count ?? data?.providers_count ?? data?.providerCount ?? routeRow?.provider_count ?? routeRow?.providers_count ?? routeRow?.providerCount;
    if (typeof value === 'number') return value;
    if (Array.isArray(routeRow?.providers)) return routeRow.providers.length;
    return 0;
  }, [data, routeRow]);

  const visitFrequency = useMemo(() => transformVisitFrequency(data), [data]);
  const noShowRisk = useMemo(() => transformNoShowRisk(data), [data]);
  const recencyBar = useMemo(() => transformRecencyFunnel(data), [data]);
  const ltvCount = useMemo(() => transformLTVCount(data), [data]);
  const acquisitionTrend = useMemo(() => transformAcquisitionTrend(data), [data]);
  const retentionGauge = useMemo(() => transformRetentionGauge(data), [data]);
  const engagementRadar = useMemo(() => transformEngagementRadar(data), [data]);
  const dailyActive = useMemo(() => transformDailyActiveCalendar(data), [data]);
  const frequencyRecency = useMemo(() => transformFrequencyRecencySankey(data), [data]);
  const ltvRevenue = useMemo(() => transformLTVRevenue(data), [data]);
  const cptDistribution = useMemo(() => transformCPTPatientDistribution(data), [data]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const visitFrequencyOption = useMemo(() => (visitFrequency ? visitFrequencyBarOption({ data: visitFrequency }) : null), [visitFrequency, chartTheme]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const noShowOption = useMemo(() => (noShowRisk ? noShowRiskDonutOption({ data: noShowRisk }) : null), [noShowRisk, chartTheme]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const recencyOption = useMemo(() => (recencyBar ? recencyHorizontalBarOption({ data: recencyBar }) : null), [recencyBar, chartTheme]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const ltvCountOption = useMemo(() => (ltvCount ? ltvCountBarOption({ data: ltvCount }) : null), [ltvCount, chartTheme]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const acquisitionOption = useMemo(() => (acquisitionTrend ? acquisitionTrendLineOption({ data: acquisitionTrend }) : null), [acquisitionTrend, chartTheme]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const retentionOption = useMemo(() => (retentionGauge ? retentionGaugeOption({ value: retentionGauge.value ?? 0 }) : null), [retentionGauge, chartTheme]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const engagementOption = useMemo(() => (engagementRadar ? engagementRadarOption({ data: engagementRadar }) : null), [engagementRadar, chartTheme]);
  const dailyCalendarOption = useMemo(() => {
    if (!dailyActive) return null;
    const points = Array.isArray(dailyActive.points) ? dailyActive.points : [];
    const s = dailyActive?.startDate ?? null;
    const e = dailyActive?.endDate ?? null;
    return dailyActiveCalendarOption({ data: points, startDate: s, endDate: e });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dailyActive, chartTheme]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const sankeyOption = useMemo(() => (frequencyRecency ? frequencyRecencySankeyOption({ nodes: frequencyRecency.nodes ?? [], links: frequencyRecency.links ?? [] }) : null), [frequencyRecency, chartTheme]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const ltvRevenueOption = useMemo(() => (ltvRevenue ? ltvRevenueTreemapOption({ data: ltvRevenue }) : null), [ltvRevenue, chartTheme]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const cptOption = useMemo(() => cptPolarEndAngleOption({data: cptDistribution,}), [cptDistribution, chartTheme]);

  const metricCards = useMemo(() => [
    { label: 'Total Patients', value: totalPatients.toLocaleString(), icon: <MdPeople size={20} className="hc-text-primary" /> },
    { label: 'Total Revenue', value: `$${Number(totalRevenue).toLocaleString()}`, icon: <MdAttachMoney size={20} className="hc-text-primary" /> },
    { label: 'Retention Rate', value: `${retentionRate}%`, icon: <MdTrendingUp size={20} className="hc-text-primary" /> },
    { label: 'Avg Revenue / Patient', value: `$${Number(avgRevenuePerPatient).toLocaleString()}`, icon: <MdShowChart size={20} className="hc-text-primary" /> },
  ], [totalPatients, totalRevenue, retentionRate, avgRevenuePerPatient]);

  const renderMetricCard = (card) => (
    <div key={card.label} className="col-12 col-sm-6 col-xl-3">
      <div className="hc-surface rounded-4 p-3 h-100 d-flex flex-column justify-content-between">
        <div className="d-flex align-items-start gap-3 ">
          <div className="d-inline-flex align-items-center justify-content-center rounded-4" style={{ width: 48, height: 48, backgroundColor: 'rgba(var(--hc-icon-navy-blue-rgb), 0.08)' }}>
            {card.icon}
          </div>
          <div>
            <div className="text-secondary small text-uppercase fw-semibold">{card.label}</div>
            <div className="fw-bold fs-4 mt-1">{card.value}</div>
          </div>
        </div>
      </div>
    </div>
  );

  if (!data && !routeRow) {
    return (
      <div className="row g-4">
        <div className="col-12">
          <div className="hc-surface rounded-4 p-4 text-center">
            <h4 className="fw-bold mb-2">No patient analytics available</h4>
            <div className="text-secondary">Patient analytics are not available for the selected context.</div>
          </div>
        </div>
      </div>
    );
  }

  const renderChart = ({ key, title, subtitle, option, height, style = {} }) => {
    const optionHasData = (opt) => {
      if (!opt) return false;
      if (Array.isArray(opt.series) && opt.series.length) {
        return opt.series.some((s) => {
          if (Array.isArray(s.data) && s.data.length) return true;
          if (Array.isArray(s.value) && s.value.length) return true;
          if (Array.isArray(s) && s.length) return true;
          if (typeof s.value === 'number') return true;
          return false;
        });
      }
      if (Array.isArray(opt.data) && opt.data.length) return true;
      return false;
    };

    const noData = !effectiveLoading && !optionHasData(option);
    return (
      <div key={key} className="col-12 col-lg-6">
        <div role="region" aria-label={title} style={style}>
          <Suspense fallback={<SkeletonCard height={height} />}>
            <BaseChart
              title={title}
              subtitle={subtitle}
              loading={effectiveLoading}
              noData={noData}
              option={option || {}}
              height={height}
            />
          </Suspense>
        </div>
      </div>
    );
  };


  return (
    <div className="container-fluid py-4">
      <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between mb-3">
        <BackButton onClick={() => navigate(-1)} />

        <div className="d-flex align-items-center gap-2">

          <CustomDatePicker
            id="analytics-start-date"
              value={startDate}
              onChange={(value) =>
                setStartDate(
                value ? new Date(value + "T00:00:00") : defaultStartDate
                )
            }
            style={{ width: 160, height: 38 }}
        />

          {/* <input
            id="analytics-start-date"
            type="date"
            className="form-control form-control-sm"
            value={toYMD(startDate)}
            onChange={(event) => {
              const next = event.target.value ? new Date(event.target.value) : defaultStartDate;
              setStartDate(next);
            }}
            style={{ width: 160, height: 38 }}
          /> */}

          <CustomDatePicker
            id="analytics-end-date"
              value={endDate}
              onChange={(value) =>
                setEndDate(
                value ? new Date(value + "T00:00:00") : defaultEndDate
                )
            }
            style={{ width: 160, height: 38 }}
        />

          {/* <input
            id="analytics-end-date"
            type="date"
            className="form-control form-control-sm"
            value={toYMD(endDate)}
            onChange={(event) => {
              const next = event.target.value ? new Date(event.target.value) : defaultEndDate;
              setEndDate(next);
            }}
            style={{ width: 160, height: 38 }}
          /> */}

          <AnalyticsChatWidget
            organizationId={organizationId}
            organizationName={organizationName || routeFacilityName}
          />
        </div>
      </div>

      {/* Row 2: Hero */}
      <div className="p-4 p-lg-5 mb-4 overflow-hidden hc-hero-banner position-relative hc-hero-banner-primary">
        <div aria-hidden="true" className="hc-hero-circle-tr position-absolute rounded-circle pe-none" />
        <div aria-hidden="true" className="hc-hero-circle-bl position-absolute rounded-circle pe-none" />

        <div className="row g-4 align-items-center position-relative">
          <div className="col-lg-8">
            <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
              <span className="px-3 py-2 rounded-pill fw-semibold bg-success text-white" style={{ fontSize: '0.82rem' }}>Analytics</span>
              <span className="px-3 py-2 rounded-pill fw-semibold" style={{ fontSize: '0.82rem', backgroundColor: 'rgba(var(--hc-white-rgb), 0.12)', color: 'var(--hc-white)' }}>{routeRow?.account_name || routeRow?.AccountName || '—'}</span>
              <span className="px-3 py-2 rounded-pill fw-semibold bg-info text-white" style={{ fontSize: '0.75rem' }}>{dateLabel || 'All Time'}</span>
            </div>

            <h2 className="fw-bold text-white mb-2" style={{ fontSize: 'clamp(1.45rem, 2.2vw, 2rem)' }}>
              {routeFacilityName || 'Patient Analytics'}
            </h2>
            <div className="row g-3 mb-0">
              <div className="col-12 col-md-6">
                <div className="d-flex align-items-center gap-3 p-3 rounded-4" style={{ backgroundColor: 'rgba(var(--hc-white-rgb), 0.15)' }}>
                  <div className="d-flex align-items-center justify-content-center rounded-3" style={{ width: 44, height: 44, backgroundColor: 'var(--hc-surface)' }}>
                    <MdPeople size={22} color="var(--hc-status-success-text)" />
                  </div>
                  <div>
                    <p className="mb-1 small text-white">Total Patients</p>
                    {effectiveLoading ? (
                      <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 28, width: '80%' }} />
                    ) : (
                      <p className="fw-bold mb-0 text-white">{totalPatients}</p>
                    )}
                  </div>
                </div>
              </div>
              <div className="col-12 col-md-6">
                <div className="d-flex align-items-center gap-3 p-3 rounded-4" style={{ backgroundColor: 'rgba(var(--hc-white-rgb), 0.15)' }}>
                  <div className="d-flex align-items-center justify-content-center rounded-3" style={{ width: 44, height: 44, backgroundColor: 'var(--hc-surface)' }}>
                    <MdAttachMoney size={22} color="var(--hc-icon-blue-alt)" />
                  </div>
                  <div>
                    <p className="mb-1 small text-white">Total Revenue</p>
                    {effectiveLoading ? (
                      <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 28, width: '80%' }} />
                    ) : (
                      <p className="fw-bold mb-0 text-white">${Number(totalRevenue).toLocaleString()}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-lg-4">
            <div className="p-3 rounded-4" style={{ backgroundColor: 'rgba(var(--hc-white-rgb), 0.10)' }}>
              {/* <div className="mb-3">
                <p className="mb-1 small" style={{ color: 'rgba(var(--hc-white-rgb), 0.65)' }}>Patient analytics overview</p>
                <p className="fw-bold text-white mb-0" style={{ fontSize: '1.3rem', lineHeight: 1.1 }}>Snapshot</p>
              </div> */}

              <div className="row g-3">
                <div className="col-12">
                  <div className="d-flex align-items-center gap-3 px-3 py-2 rounded-4" style={{ backgroundColor: 'rgba(var(--hc-white-rgb), 0.15)' }}>
                    <div className="d-flex align-items-center justify-content-center rounded-3" style={{ width: 44, height: 44, backgroundColor: 'var(--hc-surface)' }}>
                      <MdPeople size={22} color="var(--hc-status-success-text)" />
                    </div>
                    <div>
                      <p className="mb-1 small text-white">Organization</p>
                      <p className="fw-bold mb-0 text-white">{routeRow?.organization_name || routeRow?.OrganizationName || '—'}</p>
                    </div>
                  </div>
                </div>
                <div className="col-12">
                  <div className="d-flex align-items-center gap-3 px-3 py-2 rounded-4" style={{ backgroundColor: 'rgba(var(--hc-white-rgb), 0.15)' }}>
                    <div className="d-flex align-items-center justify-content-center rounded-3" style={{ width: 44, height: 44, backgroundColor: 'var(--hc-surface)' }}>
                      <MdPeople size={22} className="hc-text-primary" />
                    </div>
                    <div>
                      <p className="mb-1 small text-white">Total Providers</p>
                      <p className="fw-bold mb-0 text-white">{totalProviders}</p>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>

      {/* Row 3: Stats cards */}
      <div className="row g-3 mb-3">
        {effectiveLoading ? (
          [...Array(4)].map((_, i) => (
            <div key={i} className="col-12 col-sm-6 col-xl-3">
              <div className="hc-surface rounded-4 p-3 h-100">
                <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 18, width: '45%', marginBottom: 12 }} />
                <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 28, width: '80%' }} />
              </div>
            </div>
          ))
        ) : (
          metricCards.map(renderMetricCard)
        )}
      </div>

      {/* Row 4: Charts */}
      <div className="row g-3">
        {renderChart({ key: 'visitFrequency', title: 'Visit Frequency', subtitle: 'Patients grouped by visit frequency', option: visitFrequencyOption, height: '380px' })}
        {renderChart({ key: 'noShowRisk', title: 'No-Show Risk', subtitle: 'Patient no-show distribution', option: noShowOption, height: '380px' })}
        {renderChart({ key: 'recencyFunnel', title: 'Patient Recency', subtitle: 'Active → Lapsed', option: recencyOption, height: '380px' })}
        {renderChart({ key: 'ltvCount', title: 'Lifetime Value (Count)', subtitle: 'Patients by revenue tier', option: ltvCountOption, height: '380px' })}
        {renderChart({ key: 'acquisition', title: 'Acquisition Trend', subtitle: 'New patients per month', option: acquisitionOption, height: '380px' })}
        {renderChart({ key: 'retention', title: 'Retention Rate', subtitle: 'Active patients share', option: retentionOption, height: '380px' })}
        {renderChart({ key: 'engagement', title: 'Engagement Score', subtitle: 'Composite engagement radar', option: engagementOption, height: '380px' })}
        {renderChart({ key: 'dailyActive', title: 'Daily Active Patients', subtitle: 'Activity heatmap', option: dailyCalendarOption, height: '380px' })}
        {/* {renderChart({ key: 'sankey', title: 'Frequency → Recency', subtitle: 'Flow from frequency to recency', option: sankeyOption, height: '340px' })} */}
        {renderChart({ key: 'ltvRevenue', title: 'Lifetime Revenue Distribution', subtitle: 'Revenue by lifetime tier', option: ltvRevenueOption, height: '380px' })}
        {renderChart({key: "cpt-polar", title: "CPT Code Distribution", subtitle: "Unique patients by CPT code", option: cptOption, height: '380px',})}
      </div>
    </div>
  );
}